import React, { useEffect, useState } from "react";
import { authApi } from "../api";
import { ChevronDown, ChevronUp, Gem } from "lucide-react";

const getStonePiecesStock = (row) => Number(
  row?.total_pieces_available ??
  row?.pieces_available ??
  row?.available_pieces ??
  row?.stock ??
  0
);

const getStoneBatchPiecesStock = (batch) => Number(
  batch?.available_pieces ??
  batch?.pieces_available ??
  batch?.total_pieces_available ??
  0
);

const StoneInventory = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [batchesByMaterial, setBatchesByMaterial] = useState({});
  const [usageByMaterial, setUsageByMaterial] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);

  const loadStones = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authApi.get("/inventory/stones");
      setRows(res.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load stone inventory.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStones();
  }, []);

  const toggleExpand = async (materialId) => {
    const next = expandedId === materialId ? null : materialId;
    setExpandedId(next);
    if (!next || batchesByMaterial[materialId]) return;

    setDetailLoading(true);
    try {
      const [batchRes, usageRes] = await Promise.all([
        authApi.get(`/inventory/stones/${materialId}/batches`),
        authApi.get(`/inventory/stones/${materialId}/usage-report`),
      ]);
      setBatchesByMaterial((prev) => ({ ...prev, [materialId]: batchRes.data?.data || [] }));
      setUsageByMaterial((prev) => ({ ...prev, [materialId]: usageRes.data?.data || [] }));
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load batch details.");
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-slate-50 p-4 md:p-6">
      <div className="mb-4">
        <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Gem size={20} className="text-blue-600" /> Stone Inventory
        </h1>
        <p className="text-sm text-slate-500">
          Dedicated piece-based tracking for diamonds, rubies, emeralds and other stones.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Stone Name</th>
              <th className="px-4 py-3 text-left">Stone Type</th>
              <th className="px-4 py-3 text-left">Shape</th>
              <th className="px-4 py-3 text-right">Carat Weight</th>
              <th className="px-4 py-3 text-left">Clarity</th>
              <th className="px-4 py-3 text-left">Cut</th>
              <th className="px-4 py-3 text-left">Color</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-left">Last Receipt</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <>
                {[...Array(6)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-28" /></td>
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-24" /></td>
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-20" /></td>
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-16 ml-auto" /></td>
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-20" /></td>
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-16" /></td>
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-16" /></td>
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-14 ml-auto" /></td>
                    <td className="px-4 py-4"><div className="h-3 bg-slate-200 rounded w-24" /></td>
                    <td className="px-4 py-4"><div className="h-8 bg-slate-200 rounded w-20 ml-auto" /></td>
                  </tr>
                ))}
              </>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan="10" className="px-4 py-8 text-center text-slate-400">
                  No stone materials found.
                </td>
              </tr>
            )}
            {!loading && rows.map((row) => (
              <React.Fragment key={row.material_id}>
                <tr>
                  <td className="px-4 py-3 font-semibold text-slate-800">{row.name}</td>
                  <td className="px-4 py-3">{row.stone_type ? String(row.stone_type).replaceAll('_', ' ') : "-"}</td>
                  <td className="px-4 py-3">{row.shape || "-"}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.carat_weight ? Number(row.carat_weight).toFixed(3) : "-"}</td>
                  <td className="px-4 py-3">{row.clarity || "-"}</td>
                  <td className="px-4 py-3">{row.cut || "-"}</td>
                  <td className="px-4 py-3">{row.color || "-"}</td>
                  <td className="px-4 py-3 text-right font-mono">{getStonePiecesStock(row)}</td>
                  <td className="px-4 py-3">
                    {row.last_receipt_date ? new Date(row.last_receipt_date).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => toggleExpand(row.material_id)}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
                    >
                      Details {expandedId === row.material_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </td>
                </tr>
                {expandedId === row.material_id && (
                  <tr>
                    <td colSpan="10" className="px-4 py-4 bg-slate-50">
                      {detailLoading && !batchesByMaterial[row.material_id] ? (
                        <div className="text-sm text-slate-500">Loading batch details...</div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                              <div className="text-[11px] uppercase tracking-wide text-slate-400">Stone Type</div>
                              <div className="mt-1 font-semibold text-slate-800">{row.stone_type ? String(row.stone_type).replaceAll('_', ' ') : '-'}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                              <div className="text-[11px] uppercase tracking-wide text-slate-400">Shape</div>
                              <div className="mt-1 font-semibold text-slate-800">{row.shape || '-'}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                              <div className="text-[11px] uppercase tracking-wide text-slate-400">Carat Weight</div>
                              <div className="mt-1 font-semibold text-slate-800">{row.carat_weight ? `${Number(row.carat_weight).toFixed(3)} ct` : '-'}</div>
                            </div>
                          </div>
                          <div>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Batches</h3>
                            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-100 text-slate-500 uppercase">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Batch</th>
                                    <th className="px-3 py-2 text-right">Received</th>
                                    <th className="px-3 py-2 text-right">Issued</th>
                                    <th className="px-3 py-2 text-right">Returned</th>
                                    <th className="px-3 py-2 text-right">Available</th>
                                    <th className="px-3 py-2 text-right">Carat</th>
                                    <th className="px-3 py-2 text-left">Certificate</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {(batchesByMaterial[row.material_id] || []).map((b) => (
                                    <tr key={b.batch_id}>
                                      <td className="px-3 py-2 font-medium">{b.batch_code}</td>
                                      <td className="px-3 py-2 text-right font-mono">{b.pieces_received ?? b.total_pieces ?? 0}</td>
                                      <td className="px-3 py-2 text-right font-mono">{b.pieces_issued ?? 0}</td>
                                      <td className="px-3 py-2 text-right font-mono">{b.pieces_returned ?? 0}</td>
                                      <td className="px-3 py-2 text-right font-mono">{getStoneBatchPiecesStock(b)}</td>
                                      <td className="px-3 py-2 text-right font-mono">{Number(b.carat_weight || 0).toFixed(3)}</td>
                                      <td className="px-3 py-2">{b.certificate_number || "-"}</td>
                                    </tr>
                                  ))}
                                  {(batchesByMaterial[row.material_id] || []).length === 0 && (
                                    <tr>
                                      <td colSpan="7" className="px-3 py-4 text-center text-slate-400">No batches found.</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Usage By Job Card</h3>
                            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-100 text-slate-500 uppercase">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Job Card</th>
                                    <th className="px-3 py-2 text-left">Design</th>
                                    <th className="px-3 py-2 text-right">Pieces Used</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {(usageByMaterial[row.material_id] || []).map((u) => (
                                    <tr key={`${u.job_card_id}-${u.job_card_no}`}>
                                      <td className="px-3 py-2 font-medium">{u.job_card_no || u.job_card_id}</td>
                                      <td className="px-3 py-2">{u.design_name || "-"}</td>
                                      <td className="px-3 py-2 text-right font-mono">{u.pieces_used || 0}</td>
                                    </tr>
                                  ))}
                                  {(usageByMaterial[row.material_id] || []).length === 0 && (
                                    <tr>
                                      <td colSpan="3" className="px-3 py-4 text-center text-slate-400">No usage recorded yet.</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StoneInventory;
